// composables/usePlayer.ts
import { ref, onUnmounted, watch, nextTick } from 'vue'
import Player from '@vimeo/player'
import { useVideoLoader } from "./useVideoLoader"
const {
  videoTranscript,
} = useVideoLoader()

export function usePlayer() {
  const videoState = ref(false) // 播放狀態
  const muteStatus = ref(false) // 音訊開關
  
  const videoYt = ref<any>(null) // Youtube 播放器實例
  const originId = ref<string>('') // 影片 ID
  const progressWidth = ref('0%')
  const isDragging = ref(false)
  const cSpeed = ref<number>(1)
  let videoCt = ref(0)
  let progressInterval: any | undefined
  let youtube: Player | null = null
  let vimeo: Player | null = null
  let awsVideo: HTMLVideoElement | null = null

  const destroyYouTubePlayer = () => {
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = undefined
    }

    if (ytTimer) {
      clearInterval(ytTimer)
      ytTimer = null
    }

    // Destroy player instance
    const p: any = videoYt.value
    if (p && typeof p.destroy === 'function') {
      try { p.destroy() } catch {}
    }
    videoYt.value = null

    const el = document.getElementById('player')
    if (el) el.innerHTML = ''
  }

  // 建立 YouTube 播放器
  const createYouTubePlayer = async (id: string, onReady: () => void) => {

    destroyYouTubePlayer()

    await loadYouTubeAPI()
    const YT = (window as any).YT

    const mountEl = document.getElementById('player')
    if (!mountEl) {
      console.error('❌ 找不到 #player 容器')
      return
    }
    mountEl.innerHTML = ''

    const player = new YT.Player('player', {
      height: '360',
      width: '640',
      videoId: id,
      playerVars: {
        controls: 0,
        disablekb: 1,
        fs: 1,
        loop: 1,
        rel: 0,
        showinfo: 0,
        mute: 1,
        autohide: 1,
        iv_load_policy: 3,
        playsinline: 1
      },
      events: {
        onReady: () => {
          onReady()
          playVideo()
        },
        onStateChange: handleYTStateChange
      }
    })

  videoYt.value = player
}

  // 建立 Vimeo 播放器
  const createVimeoPlayer = (id: any) => {
    if (!id) {
    console.error("❌ createVimeoPlayer: id 為空")
    return
  }

      originId.value = id 
    vimeo = new Player('player', {
      id,
      autoplay: false,
      muted: true,             // 靜音（對齊 YT mute: 1）
      loop: true,              // 自動重播，對齊 YT loop: 1
      responsive: true,        // 適應容器寬高
      title: false,            // 不顯示標題
      byline: false,           // 不顯示作者
      portrait: false          // 不顯示大頭貼
    })

    console.log("🧪 Vimeo player 建立完成？", vimeo, id)

    vimeo.ready().then(() => {
      vimeo?.setVolume(0).then(() => {
        muteStatus.value = false
      })
    })

    vimeo.play()
      .then(() => {
        console.log("✅ Vimeo 播放成功")
        videoState.value = true
        setupVimeoEvents()
      })
      .catch((e: any) => {
        console.error("❌ Vimeo 播放失敗:", e)
        videoState.value = false
      })


  }

  // 建立 AWS 播放器
  const createAwsPlayer = (url: string) => {
  if (!url) {
    console.error('❌ createAwsPlayer: url 為空')
    return
  }

  originId.value = url

  // 清空舊內容
  const container = document.getElementById('player')
  if (!container) {
    console.error('❌ 找不到 #player 容器')
    return
  }
  container.innerHTML = ''

  // 動態建立 <video>
  const v = document.createElement('video')
  v.setAttribute('playsinline', '')
  v.setAttribute('webkit-playsinline', '')
  v.preload = 'metadata'
  v.muted = true            // 為符合自動播放策略
  v.autoplay = true
  v.loop = true
  v.controls = false        // 走你自訂 UI
  v.src = url

  // 讓 <video> 填滿容器（你的 CSS 若已處理可拿掉）
  v.style.position = 'absolute'
  v.style.inset = '0'
  v.style.width = '100%'
  v.style.height = '100%'

  container.appendChild(v)
  awsVideo = v

  console.log('🧪 AWS player 建立完成？', awsVideo, url)

  // 事件：同步到你既有的 reactive 狀態
  v.addEventListener('playing', () => { videoState.value = true })
  v.addEventListener('pause',   () => { videoState.value = false })
  v.addEventListener('ended',   () => { videoState.value = false })
  v.addEventListener('timeupdate', () => {
    if (!isDragging.value && v.duration) {
      videoCt.value = v.currentTime
      progressWidth.value = `${(v.currentTime / v.duration) * 100}%`
    }
  })

  // 嘗試播放
  v.play()
    .then(() => {
      console.log('✅ AWS 播放成功')
      videoState.value = true
      muteStatus.value = false // 你的語意：false = 靜音、true = 有聲
      setupAwsEvents()
    })
    .catch((e) => {
      console.error('❌ AWS 播放失敗:', e)
      videoState.value = false
    })
}

  const setupVimeoEvents = () => {
    if (!vimeo) return

    vimeo.on('play', () => {
      videoState.value = true
    })

    vimeo.on('pause', () => {
      videoState.value = false
      console.log('⏸️ Vimeo 暫停')
    })

    vimeo.on('ended', () => {
      videoState.value = false
      console.log('🏁 Vimeo 結束')
    })

    /* 每0.5秒回報一次現在影片播放的位置 */
    setInterval(() => {
        if (vimeo) {
          vimeo.getCurrentTime().then((time: number) => {
            videoCt.value = time
          })
        }
      }, 500)
  }

  const setupAwsEvents = () => {
  if (!awsVideo) return
  // 每 0.5 秒更新一次 currentTime（你也可只依賴 timeupdate）
  setInterval(() => {
    if (awsVideo) videoCt.value = awsVideo.currentTime || 0
  }, 500)
}

  // 處理 YouTube 播放狀態變更
  const handleYTStateChange = (event: any) => {
    if (event.data === 1) videoState.value = true
    else if (event.data === 2 || event.data === 0) videoState.value = false

    const player = event?.target
    if (!player || typeof player.getCurrentTime !== 'function') return

    if (!progressInterval) {
      progressInterval = setInterval(() => {
        if (!isDragging.value && player && typeof player.getCurrentTime === 'function') {
          const curr = player.getCurrentTime()
          const total = player.getDuration?.() ?? 0
          progressWidth.value = total > 0 ? `${(curr / total) * 100}%` : '0%'
          videoCt.value = Number(curr) || 0
        }
      }, 500)
    }
  }

  // 載入 YT API
  const loadYouTubeAPI = (): Promise<void> => {
    return new Promise((resolve) => {
      if ((window as any).YT && typeof (window as any).YT.Player === 'function') {
        resolve()
        return
      }
      if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return resolve()
      
      //如果沒載入，動態加 script 標籤
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(tag)

      //每 100ms 檢查一次是否 YT 已準備好
      const checkYT = setInterval(() => {
        if ((window as any).YT && typeof (window as any).YT.Player === 'function') {
          clearInterval(checkYT)
          resolve()
        }
      }, 100)

      setInterval(() => {
        if (videoYt.value && typeof videoYt.value.getCurrentTime === 'function') {
          videoCt.value = videoYt.value.getCurrentTime()
        }
      }, 500)

    })
  }

  // 播放與暫停共用函式
  const toggleVideo = () => {
    if (videoState.value) pauseVideo()
    else playVideo()
  }

  const playVideo = () => {
    if (videoYt.value) {
      videoYt.value.playVideo()
    } else if (vimeo) {
      videoState.value = true

      vimeo.play()
        .then(() => {
          console.log("✅ Vimeo 播放成功")
          videoState.value = true
        })
        .catch((e: any) => {
          console.error("❌ Vimeo 播放失敗:", e)
          videoState.value = false
        })
    } else if (awsVideo) { awsVideo.play() } // ✅

  }

  const pauseVideo = () => {
    if (videoYt.value) {
      videoYt.value.pauseVideo()
      videoState.value = false
    } else if (vimeo) {
      vimeo.pause()
        .then(() => {
          console.log("⏸️ Vimeo 暫停成功")
          videoState.value = false
        })
        .catch((e: any) => {
          console.error("❌ Vimeo 暫停失敗:", e)
        })
    } else if (awsVideo) { awsVideo.pause(); videoState.value = false } // ✅

  }


  const mute = () => {
    if (videoYt.value && typeof videoYt.value.isMuted === 'function') {
      if (videoYt.value.isMuted()) {
        videoYt.value.unMute()
        muteStatus.value = true
      } else {
        videoYt.value.mute()
        muteStatus.value = false
      }
    } else if (vimeo) {
      vimeo.getVolume().then((volume: number) => {
        const shouldUnmute = volume === 0 
        console.log(volume,muteStatus.value)

        muteStatus.value = !shouldUnmute  
        vimeo?.setVolume(shouldUnmute ? 1 : 0) // 設定聲音（0 表示靜音，1 表示開聲）

          .then(() => {
            muteStatus.value = shouldUnmute 
  })
      }).catch((err) => {
        console.warn('⚠️ 無法取得 Vimeo 音量', err)
      })
    } else if (awsVideo) { // ✅
      awsVideo.muted = !awsVideo.muted
      muteStatus.value = !awsVideo.muted
    }

  }


  const canChangeSpeed = ref(true) // 是否支援調整播放速度？

  const getVoice = (speed: number) => {
    // YouTube 處理
    if (videoYt.value && typeof videoYt.value.getPlaybackRate === 'function') {
      const ytSpeed = videoYt.value.getPlaybackRate()
      cSpeed.value = ytSpeed >= 1 ? 0.25 : +(ytSpeed + 0.25).toFixed(2)
      videoYt.value.setPlaybackRate(cSpeed.value)
    }
    else if (vimeo && typeof vimeo.getPlaybackRate === 'function') {
      vimeo.getPlaybackRate()
        .then((vimeoSpeed: number) => {
          canChangeSpeed.value = true
          if (vimeoSpeed === 1) {
            cSpeed.value = 0.25
          } else {
            cSpeed.value = speed + 0.25
          }

          try {
            vimeo?.setPlaybackRate(cSpeed.value)
          } catch (err) {
            console.warn("⚠️ Vimeo 此影片不支援播放速度調整", err)
            canChangeSpeed.value = false
          }
        })
        .catch((err) => {
          console.warn("⚠️ Vimeo 無法取得播放速度", err)
          canChangeSpeed.value = false
        })
    }else if (awsVideo) {                   // ✅
      const curr = awsVideo.playbackRate || 1
      const next = curr >= 1 ? 0.25 : +(curr + 0.25).toFixed(2)
      awsVideo.playbackRate = next
      cSpeed.value = next
      canChangeSpeed.value = true
    }

  }

  



  /* 跳轉到指定時間播放 */
  function jumpToTime(time: number, height: number) {
    const subtitle = document.getElementById('subtitle') as HTMLElement
    subtitle.scrollTop = height
    if (videoYt.value) {
      videoYt.value.seekTo(time)
    } else if (vimeo) {
      vimeo?.setCurrentTime(time)
    } else if (awsVideo) { // 如果你用的是 mp4Video 這個變數，就改成 mp4Video
      const dur = awsVideo.duration || 0
      // 可選：避免超出範圍
      const t = dur ? Math.max(0, Math.min(time, dur)) : time
      awsVideo.currentTime = t
    }

    playVideo()
  }

  /* 點擊字幕播放時 */
  function handleAudioClick(item: any, index: number) {
    item.state ? pauseVideo() : jumpToTime(item.start, item.ht)
    item.state = !item.state
  }

  /*  單句字幕播放的起始時間 */
  function formatDuration(seconds:any) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;

  }
  const handleProgressBarClick = async (event: MouseEvent) => {
    const progressBar = event.currentTarget as HTMLElement
    const progressBarRect = progressBar.getBoundingClientRect()
    const clickedPosition = event.clientX - progressBarRect.left

    const progressBarWidth = progressBar.clientWidth
    const newProgress = clickedPosition / progressBarWidth

    // ✅ YouTube
    if (videoYt.value && typeof videoYt.value.getDuration === 'function') {
      const duration = videoYt.value.getDuration()
      const newTime = newProgress * duration
      videoYt.value.seekTo(newTime, true)
      progressWidth.value = `${newProgress * 100}%`
    }

    // ✅ Vimeo
    else if (vimeo && typeof vimeo.getDuration === 'function') {
      try {
        const duration = await vimeo.getDuration()
        const newTime = newProgress * duration
        await vimeo.setCurrentTime(newTime)
        progressWidth.value = `${newProgress * 100}%`
      } catch (err) {
        console.error('❌ Vimeo 設定播放進度失敗:', err)
      }
    }else if (awsVideo?.duration) {           // ✅
  awsVideo.currentTime = newProgress * awsVideo.duration
}

  }

  const startDragging = () => {
    isDragging.value = true
  }

  const handleDrag = (event: MouseEvent) => {
    if (!isDragging.value) return

    const progressBar = event.currentTarget as HTMLElement
    const progressBarRect = progressBar.getBoundingClientRect()
    const draggedPosition = event.clientX - progressBarRect.left

    updateProgress(draggedPosition)
  }

  const stopDragging = () => {
    isDragging.value = false
  }

  const updateProgress = async (position: number) => {

    const progressBarElement = document.getElementById('progressbar') as HTMLElement | null
    if (!progressBarElement) return

    const progressBarWidth = progressBarElement.clientWidth
    const newProgress = position / progressBarWidth
    const progressPercent = `${newProgress * 100}%`
    const newTimeRef = ref()

    // 🎯 YouTube 處理
    if (videoYt.value && typeof videoYt.value.getDuration === 'function') {
      const duration = videoYt.value.getDuration()
      const newTime = newProgress * duration
      newTimeRef.value = newTime
      videoYt.value.seekTo(newTime, true)
      progressWidth.value = progressPercent
    }

    // 🎯 Vimeo 處理
    else if (vimeo && typeof vimeo.getDuration === 'function') {
      try {
        const duration = await vimeo.getDuration()
        const newTime = newProgress * duration
              newTimeRef.value = newTime

        await vimeo.setCurrentTime(newTime)
        progressWidth.value = progressPercent
      } catch (err) {
        console.error('❌ Vimeo 設定播放進度失敗:', err)
      }
    }
    // 🎯 AWS / HTML5 <video>
    else if (awsVideo && typeof awsVideo.duration === 'number') {
      const duration = awsVideo.duration || 0
      const newTime = newProgress * duration
      newTimeRef.value = newTime
      awsVideo.currentTime = newTime
      progressWidth.value = progressPercent
    }
    
      const subtitleEl = document.getElementById('subtitle') as HTMLElement | null
      if (subtitleEl && Array.isArray(videoTranscript.value)) {
        const active = videoTranscript.value.find(
          (item: any) => newTimeRef.value >= item.start && newTimeRef.value <= item.end
        )
        if (active) {
          subtitleEl.scrollTop = active.ht
        }
      }
  }

    
    // 未開始播放 (Unstarted): -1
    // 正在播放 (Playing): 1
    // 已暫停 (Paused): 2
    // 已結束 (Ended): 0
    // 緩衝中 (Buffering): 3
    // 已放好 (Video cued): 5

  let ytTimer: ReturnType<typeof setInterval> | null = null

  const onPlayerStateChange = (event: any): void => {
  const state = event.data
  console.log('Player State:', state)

  if (state === 1) {
    // ▶️ 播放中
    videoState.value = true

    if (!ytTimer) {
      ytTimer = setInterval(() => {
        if (!isDragging.value && videoYt.value) {
          const currTime = videoYt.value.getCurrentTime()
          const duration = videoYt.value.getDuration()
          videoCt.value = Number(currTime)

          const progress = (currTime / duration) * 100
          progressWidth.value = `${progress}%`
        }
      }, 500)
    }

  } else if (state === 2 || state === 0) {
    // ⏸️ 暫停 或 ⏹️ 結束
    videoState.value = false

    if (ytTimer) {
      clearInterval(ytTimer)
      ytTimer = null
    }
  }
}




    // 清除 interval 與播放器
  onUnmounted(() => {
    if (progressInterval) clearInterval(progressInterval)
    if (videoYt.value?.destroy) videoYt.value.destroy()
    if (vimeo?.destroy) vimeo.destroy()

    if (awsVideo) {
    try {
      awsVideo.pause()
      awsVideo.src = ''
      awsVideo.load()
      awsVideo.remove()
    } catch {}
    awsVideo = null
  }

  })

  return {
    createYouTubePlayer,
    createVimeoPlayer,
    createAwsPlayer,
    toggleVideo,
    playVideo,
    pauseVideo,
    formatDuration,
    onPlayerStateChange,
    handleAudioClick,
    getVoice,
    mute,
    handleProgressBarClick,
    startDragging,
    handleDrag,
    stopDragging,
    updateProgress,
    cSpeed,
    canChangeSpeed,
    muteStatus,
    videoState,
    originId,
    progressWidth,
    videoCt,
    videoYt,
    vimeo,
    isDragging
  }
} 
